import type { Tables } from "../../database.types";

export type ShopProductVariantRecord = Pick<
  Tables<"shop_product_variants">,
  "id" | "label" | "available_quantity" | "deleted_at"
>;

export type ShopProductRecord = Pick<
  Tables<"shop_products">,
  | "id"
  | "name"
  | "description"
  | "main_image_url"
  | "price_cents"
  | "deleted_at"
  | "display_order"
  | "created_at"
> & {
  shop_product_variants: ShopProductVariantRecord[] | null;
};

export type ShopProductDetailVariantRecord = ShopProductVariantRecord &
  Pick<Tables<"shop_product_variants">, "sort_order">;

export type ShopProductDetailRecord = ShopProductRecord &
  Pick<Tables<"shop_products">, "gallery_image_urls"> & {
    shop_product_variants: ShopProductDetailVariantRecord[] | null;
  };

export type ShopCatalogueProduct = {
  id: string;
  name: string;
  description: string;
  mainImageUrl: string;
  priceCents: number;
  priceLabel: string;
  availableQuantity: number;
  availabilityLabel: string;
  isSoldOut: boolean;
};

export type ShopProductVariant = {
  id: string;
  label: string;
  availableQuantity: number;
  availabilityLabel: string;
  isSoldOut: boolean;
};

export type ShopProductDetail = ShopCatalogueProduct & {
  galleryImageUrls: string[];
  variants: ShopProductVariant[];
};

const MAX_SHOP_GALLERY_IMAGES = 5;

export const formatShopPrice = (priceCents: number) => {
  if (priceCents === 0) {
    return "GRATIS";
  }

  const amount = new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(priceCents / 100);

  return `€ ${amount}`;
};

const formatAvailability = (availableQuantity: number) => {
  if (availableQuantity <= 0) {
    return "ESAURITO";
  }

  if (availableQuantity === 1) {
    return "1 DISPONIBILE";
  }

  return `${availableQuantity} DISPONIBILI`;
};

const calculateAvailableQuantity = (
  variants: ShopProductVariantRecord[] | null,
) => {
  return (variants ?? [])
    .filter((variant) => !variant.deleted_at)
    .reduce((total, variant) => {
      return total + variant.available_quantity;
    }, 0);
};

export const mapShopCatalogueProducts = (
  products: ShopProductRecord[],
): ShopCatalogueProduct[] => {
  return products
    .filter((product) => !product.deleted_at)
    .map((product) => {
      const availableQuantity = calculateAvailableQuantity(
        product.shop_product_variants,
      );

      return {
        id: product.id,
        name: product.name,
        description: product.description,
        mainImageUrl: product.main_image_url,
        priceCents: product.price_cents,
        priceLabel: formatShopPrice(product.price_cents),
        availableQuantity,
        availabilityLabel: formatAvailability(availableQuantity),
        isSoldOut: availableQuantity <= 0,
      };
    });
};

const mapVariants = (
  variants: ShopProductDetailVariantRecord[] | null,
): ShopProductVariant[] => {
  return (variants ?? [])
    .filter((variant) => !variant.deleted_at)
    .sort((first, second) => first.sort_order - second.sort_order)
    .map((variant) => {
      return {
        id: variant.id,
        label: variant.label,
        availableQuantity: variant.available_quantity,
        availabilityLabel: formatAvailability(variant.available_quantity),
        isSoldOut: variant.available_quantity <= 0,
      };
    });
};

export const mapShopProductDetail = (
  product: ShopProductDetailRecord | null,
): ShopProductDetail | null => {
  if (!product || product.deleted_at) {
    return null;
  }

  const [catalogueProduct] = mapShopCatalogueProducts([product]);

  if (!catalogueProduct) {
    return null;
  }

  return {
    ...catalogueProduct,
    galleryImageUrls: (product.gallery_image_urls ?? []).slice(
      0,
      MAX_SHOP_GALLERY_IMAGES,
    ),
    variants: mapVariants(product.shop_product_variants),
  };
};

/**
 * Keeps a requested quantity inside what the selected variant can sell.
 * Switching variant keeps the quantity the buyer already picked, trimmed down
 * when the new variant has less stock.
 */
export const clampPurchaseQuantity = (
  quantity: number,
  availableQuantity: number,
) => {
  return Math.min(Math.max(quantity, 1), availableQuantity);
};
